import type { FC } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { AutoCompleteStyle } from "../../documentation/styles/InputStyle";
import type { AntCustomFormField } from "../Form/types/types";
import { useGetSearchAutoCompleteQuery } from "../Table";

/**
 * Defines how much time will be delayed the backend request
 * when an user is typing in an Autocomplete Component.
 */
export const TYPING_DELAY_MS = 400;

interface AutoCompleteProps extends AntCustomFormField<string> {
    label: string;
    readOnly: boolean;
    displayValue: string;
    datasource: string;
    onSelectedOption?: Function;
    dataMapperKey?: string;
    params?: string;
}

export const Autocomplete: FC<AutoCompleteProps> = ({
    onChange,
    label,
    readOnly,
    displayValue,
    value,
    datasource,
    onSelectedOption,
    dataMapperKey,
    params,
}) => {
    const [searchString, setSearchString] = useState<string>(value || "");
    const [query, setQuery] = useState("");
    const [typeInterval, setTypeInterval] = useState(setInterval(() => {}, 0));
    const [isTyping, setIsTyping] = useState(true);
    const [selectedValue, setSelectedValue] = useState<object | undefined>(undefined);

    const { data: autoCompleteData, isLoading } = useGetSearchAutoCompleteQuery({ resourceFetchPath: datasource, query, dataMapperKey });

    useEffect(() => {
        if (onSelectedOption && selectedValue && searchString !== "" && searchString !== value) {
            onSelectedOption(selectedValue);
            setSelectedValue(undefined);
        }
    }, [selectedValue, onSelectedOption, searchString, value]);

    useEffect(() => {
        if (isTyping) {
            return;
        }
        const columnQueryParam = `${displayValue}=${searchString}`;
        let uriParams = `${columnQueryParam}&pageSize=20&pageNumber=0`;
        if (params) {
            uriParams += `&${uriParams}`;
        }
        setQuery(uriParams);
    }, [isTyping, displayValue, searchString, params]);

    useEffect(() => {
        // While user is typing we do not want to make a request on each letter.
        // Interval is used to delay the backend request.
        const interval = setInterval(() => {
            if (searchString && !isLoading && !selectedValue) {
                setIsTyping(false);
            }
        }, TYPING_DELAY_MS);

        setTypeInterval(interval);
    }, [searchString, isLoading, selectedValue]);

    const onSelect = (str: string, option: Record<string, string>) => {
        clearInterval(typeInterval);
        setIsTyping(true);
        const optionAsCompleteObject = autoCompleteData?.find((opt) => opt[displayValue as keyof typeof opt] === option.value);
        setSelectedValue(optionAsCompleteObject);
        if (onChange) {
            onChange(str);
        }
    };

    const onTyping = (str: string) => {
        clearInterval(typeInterval);
        setIsTyping(true);
        setSearchString(str);
        setSelectedValue(undefined);
    };
    const autoCompleteOptions = autoCompleteData?.map((obj) => ({ value: obj[displayValue as keyof typeof obj] }));

    const { t } = useTranslation();

    return (
        <AutoCompleteStyle
            placeholder={t(label || "")}
            onSelect={onSelect}
            onChange={onTyping}
            value={searchString}
            options={autoCompleteOptions || []}
            allowClear
            disabled={readOnly}
            notFoundContent={t("list.form.noRecords")}
        />
    );
};
